import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { NavBarContent } from '@components/Navbar';
import { HeadTags, NavBarContentData } from '@/shared/types/home.types';
import { api, mimir, site, validatesResponse } from '@/services/api';
import { TimeLine, WidgetType } from '@/types/types';
import { BreadcrumbItem } from '@/components/Layout/Breadcrumb';
import { AxiosResponse } from 'axios';
import { SchemaType } from '@/components/Internals/InternalSchema';
import { useState } from 'react';
import { GalleryI, ImageI, EmbedI, CitacaoI, AspasI, ProsAndConsI } from '@/components/Internals/CustomComponentComposer/interfaces';
import { ZoomPrices } from '@/components/Layout/DealsBox';
import PageDataContext from '@/contexts/pageData';
import useBreakpoint from '@/hooks/useBreakpoint';
import { AdMgidProps } from '@/components/Ads/AdMgid';
import Layout from '@/components/Layout';

const AdMgid = dynamic(() => import('@/components/Ads/AdMgid'), { ssr: false });
const NewsItem = dynamic(() => import('@/components/Internals/NewsItem'));

export type partHtml = GalleryI | ImageI | EmbedI | CitacaoI | AspasI | ProsAndConsI | GenericPartHtml;

export interface GenericPartHtml {
  tipo: 'adMobile' | 'adDesktop' | 'adTruvidMobile' | 'adTruvidDestop' | string;
  valor: Array<string | partHtml>;
  tag?: keyof JSX.IntrinsicElements;
}

// TIPO_NOTICIAS = 1;
// TIPO_ANALISES = 2;
// TIPO_DICAS = 3;
// TIPO_TUTORIAIS = 4;
// TIPO_O_QUE_E = 5;
// TIPO_MATERIAS = 6;
// TIPO_COLUNAS = 7;
// TIPO_ARTIGOS = 8;
// TIPO_EDITORIAIS = 10;
//  TIPO_LISTAS = 11;
type PublicationTypes = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '10' | '11';

export type ArticleType = {
  id: string;
  timeline: number;
  cu: number;
  share: string;
  datalayer: string;
  videodestaque: string;
  patrocinado: string;
  conteudo: partHtml[];
  widget: WidgetType;
  blocoBlackFriday: boolean;
  botoes?: any;
  tipoPublicacao?: PublicationTypes;
};

export type FeaturedNewsApi = {
  data: any;
  ssr: HeadTags[];
  segmentacoes?: [];
  headers: {
    'Cache-control': string;
    Expires: string;
  };
  breadcrumb?: BreadcrumbItem[];
  paginacao?: string;
  schema?: SchemaType;
};

export type NewsDataType = {
  dominio: string;
  flags: number;
  titulo: string;
  tituloAlt: string;
  cu: string;
  oferta: boolean;
  publieditorial: boolean;
  article: ArticleType;
  tipo?: string;
  categorias: string[];
  categoriaPrincipal: string;
  analises?: TimeLine[];
  ranking?: TimeLine[];
  moreTimelines?: TimeLine[];
  breadcrumb?: BreadcrumbItem[];
  segmentacoes?: [];
  maisAssistidos?: TimeLine[];
  ultimosPodcasts?: TimeLine[];
  url: string;
  paginacao?: string;
  capa: {
    blurhash: string;
    creditos: string;
    srcset: { [key: number]: string };
    url: string;
    dimensao: {
      largura: number;
      altura: number;
    };
  };
  publicacao: {
    autor: {
      nome: string;
      link: string;
    };
    editor: {
      nome: string;
      link: string;
    };
    data: string;
    dataPublicacao: string;
    dataAtualizacao: string;
  };
  precos?: ZoomPrices[];
};

export interface NewsType {
  navBarData: NavBarContent;
  responseData: {
    data: NewsDataType;
    segmentacoes: segmentacao[];
    breadcrumb: BreadcrumbItem[];
    ssr: HeadTags[];
    paginacao?: string;
    schema: SchemaType;
  };
}

type segmentacao = {
  chave: string;
  valor: string | [];
};

export default function News({ navBarData, responseData }: NewsType): JSX.Element {
  const [pageData, setPageData] = useState(responseData);

  const { data, ssr, schema } = responseData;
  const { segmentacoes, tipo, flags } = data;

  const isAdvertorial = tipo === 'artigo' && (flags & 2) !== 0;

  const [isTabletOrMobile] = useBreakpoint('tablet');

  const typeMgid: AdMgidProps['type'] = `${isTabletOrMobile ? 'mobile' : 'desktop'}`;

  const dataLayer = {
    category: data?.categoriaPrincipal || null,
    author: data?.publicacao?.autor?.nome || null,
    editor: data?.publicacao?.editor?.nome || null,
    contentType: data?.tipo || null,
  };

  return (
    <PageDataContext.Provider value={{ pageData, setPageData }}>
      <Layout
        layoutData={{ segmentacoes, tagsData: ssr, navBarData, schema, contentType: data.tipo, breadcrumbList: data.breadcrumb }}
        isAdvertorial={isAdvertorial}
        dataLayer={dataLayer}>
        <NewsItem newsData={data} type={tipo} isCurrentPage />
        {!isAdvertorial && <AdMgid type={typeMgid} />}
      </Layout>
    </PageDataContext.Provider>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { categorias, news = [] } = context.query;
  const { host, referer } = context.req.headers;
  let treatedResponse;
  let data = null;
  let dataMimir = [];
  let statusCode = 0;

  const homeReq = site.get<FeaturedNewsApi>(`/${categorias}/${news[0]}/`);
  const responseMenuReq = api.get<NavBarContentData>(`/componentes/menu/`);
  const promises = { homeReq, responseMenuReq };
  const promiseData: {
    homeReq: AxiosResponse<FeaturedNewsApi>;
    responseMenuReq: AxiosResponse<NavBarContentData>;
  } = {
    homeReq: null,
    responseMenuReq: null,
  };

  await Promise.allSettled(Object.values(promises)).then(responses =>
    responses.forEach((response, index) => {
      if (response.status === 'fulfilled') {
        switch (index) {
          case 0:
            promiseData.homeReq = (response as PromiseFulfilledResult<AxiosResponse<FeaturedNewsApi>>).value;
            treatedResponse = validatesResponse(response.value, false, false, '', host, referer, `/${categorias}/${news[0]}/`);
            data = treatedResponse.data;
            statusCode = treatedResponse.statusCode;
            break;
          case 1:
            promiseData.responseMenuReq = (response as PromiseFulfilledResult<AxiosResponse<NavBarContentData>>).value;
            break;
          default:
            break;
        }
      }
      if (index === 0 && response.status === 'rejected') {
        treatedResponse = validatesResponse(response.reason, true, false, '', host, referer, `/${categorias}/${news[0]}/`);
        data = treatedResponse.data;
        statusCode = treatedResponse.statusCode;
      }
    })
  );

  if (data.redirect || data.notFound) return data;

  context.res.setHeader('Cache-Control', data.headers['Cache-control']);
  context.res.setHeader('Expires', data.headers.Expires);
  context.res.statusCode = statusCode;

  if (statusCode === 410) {
    context.res.end();
  }

  const isAdvertorial = data?.data?.tipo === 'artigo' && (data?.data?.flags & 2) !== 0;

  if (isAdvertorial) {
    await Promise.all([
      mimir.get<Pick<FeaturedNewsApi, 'data'>>(`/timelines/mais/lidas/1-dia`),
      mimir.get<Pick<FeaturedNewsApi, 'data'>>(`/timelines/mais/lidas/1-dia?p=2`),
    ])
      .then(response => {
        dataMimir = [...response[0]?.data?.data?.timeline?.itens, ...response[1]?.data?.data?.timeline?.itens?.slice(0, 6)];
      })
      .catch(err => {
        console.log(err);
      });

    if (data?.data) data.data.ranking = dataMimir;
  }

  const responseData = data;
  const navBarData = promiseData.responseMenuReq.data.data;

  return {
    props: {
      responseData,
      navBarData,
    },
  };
};
